import { supabase } from "@/integrations/supabase/client";
import type { 
  BetaTester, 
  BetaTesterWithUser, 
  BetaTestingResponse, 
  BetaTestingStats,
  BetaTestingSettings,
  FeedbackSubmission,
  UserFeedback,
  UserFeedbackWithUser
} from "@/types/beta-testing";

// Transform from Supabase format to our app format
const transformBetaTester = (row: any): BetaTester => ({
  id: row.id,
  user_id: row.user_id,
  joined_at: row.joined_at,
  created_at: row.created_at,
  updated_at: row.updated_at,
});

// Transform beta tester with user data
const transformBetaTesterWithUser = (row: any): BetaTesterWithUser => ({
  ...transformBetaTester(row),
  user: {
    id: row.user.id,
    email: row.user.email,
    name: row.user.name,
    avatar_url: row.user.avatar_url,
  },
});

// Transform user feedback with user data
const transformUserFeedbackWithUser = (row: any): UserFeedbackWithUser => ({
  id: row.id,
  user_id: row.user_id,
  context: row.context,
  feedback_text: row.feedback_text,
  submitted_at: row.submitted_at,
  app_version: row.app_version,
  platform: row.platform,
  image_urls: row.image_urls || [],
  created_at: row.created_at,
  updated_at: row.updated_at,
  user: {
    id: row.user.id,
    email: row.user.email,
    name: row.user.name,
    avatar_url: row.user.avatar_url,
  },
});

// Join beta testing program
export const joinBetaTesting = async (): Promise<BetaTestingResponse> => {
  try {
    // Get current user profile
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      throw new Error("User not authenticated");
    }

    const { data: profile } = await supabase
      .from("profiles")
      .select("id")
      .eq("uid", user.id)
      .single();

    if (!profile) {
      throw new Error("User profile not found");
    }

    // Check if user is already a beta tester
    const { data: existingTester } = await supabase
      .from("beta_testers")
      .select("id")
      .eq("user_id", profile.id)
      .single();

    if (existingTester) {
      return {
        success: false,
        message: "You are already a beta tester!",
      };
    }

    // Insert beta tester record
    const { data: betaTester, error } = await supabase
      .from("beta_testers")
      .insert({
        user_id: profile.id,
      })
      .select()
      .single();

    if (error) throw error;

    return {
      success: true,
      message: "Welcome to beta testing! Please provide feedback when you're ready.",
      beta_tester: transformBetaTester(betaTester),
    };
  } catch (error) {
    console.error("Error joining beta testing:", error);
    return {
      success: false,
      message: "Failed to join beta testing. Please try again.",
    };
  }
};

// Get current user's beta testing status
export const getCurrentUserBetaStatus = async (): Promise<BetaTester | null> => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return null;

    const { data: profile } = await supabase
      .from("profiles")
      .select("id")
      .eq("uid", user.id)
      .single();

    if (!profile) return null;

    const { data: betaTester } = await supabase
      .from("beta_testers")
      .select("*")
      .eq("user_id", profile.id)
      .single();

    return betaTester ? transformBetaTester(betaTester) : null;
  } catch (error) {
    console.error("Error getting current user beta status:", error);
    return null;
  }
};

// Check if a specific user is a beta tester by user ID
export const isUserBetaTester = async (userId: number): Promise<boolean> => {
  try {
    const { data: betaTester } = await (supabase as any)
      .from("beta_testers")
      .select("id")
      .eq("user_id", userId)
      .single();

    return !!betaTester;
  } catch (error) {
    console.error("Error checking if user is beta tester:", error);
    return false;
  }
};

// Make a user a beta tester
export const makeUserBetaTester = async (userId: number): Promise<{ success: boolean; message: string }> => {
  try {
    // Check if user is already a beta tester
    const { data: existingTester } = await (supabase as any)
      .from("beta_testers")
      .select("id")
      .eq("user_id", userId)
      .single();

    if (existingTester) {
      return {
        success: false,
        message: "User is already a beta tester!",
      };
    }

    // Insert beta tester record
    const { data: betaTester, error } = await (supabase as any)
      .from("beta_testers")
      .insert({
        user_id: userId,
      })
      .select()
      .single();

    if (error) throw error;

    return {
      success: true,
      message: "User has been successfully added as a beta tester!",
    };
  } catch (error) {
    console.error("Error making user beta tester:", error);
    return {
      success: false,
      message: "Failed to make user a beta tester. Please try again.",
    };
  }
};

// Submit feedback to user_feedback table
export const submitFeedback = async (feedback: FeedbackSubmission): Promise<BetaTestingResponse> => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      throw new Error("User not authenticated");
    }

    const { data: profile } = await supabase
      .from("profiles")
      .select("id")
      .eq("uid", user.id)
      .single();

    if (!profile) {
      throw new Error("User profile not found");
    }

    // Insert feedback into user_feedback table
    const { data: userFeedback, error } = await supabase
      .from("user_feedback")
      .insert({
        user_id: profile.id,
        context: "beta_testing",
        feedback_text: feedback.feedback_text,
        app_version: feedback.app_version,
        platform: feedback.platform,
        image_urls: feedback.image_urls || [],
      })
      .select()
      .single();

    if (error) throw error;

    return {
      success: true,
      message: "Thank you for your feedback!",
    };
  } catch (error) {
    console.error("Error submitting feedback:", error);
    return {
      success: false,
      message: "Failed to submit feedback. Please try again.",
    };
  }
};

// Get all beta testers with their feedback (admin only)
export const getAllBetaTesters = async (): Promise<BetaTesterWithUser[]> => {
  try {
    const { data, error } = await supabase
      .from("beta_testers")
      .select(`
        *,
        user:profiles!beta_testers_user_id_fkey (
          id,
          email,
          name,
          avatar_url
        )
      `)
      .order("joined_at", { ascending: false });

    if (error) throw error;
    return (data || []).map(transformBetaTesterWithUser);
  } catch (error) {
    console.error("Error fetching beta testers:", error);
    throw error;
  }
};

// Filter and pagination interfaces
export interface UserFeedbackFilters {
  search?: string;
  context?: string;
  platform?: string;
  dateRange?: string;
  hasImages?: string;
  sortBy?: string;
}

export interface PaginationParams {
  page: number;
  pageSize: number;
}

export interface UserFeedbackResponse {
  data: UserFeedbackWithUser[];
  total: number;
  totalPages: number;
  currentPage: number;
  pageSize: number;
}

// Get user feedback with server-side filtering and pagination (admin only)
export const getBetaTestingFeedback = async (
  pagination: PaginationParams = { page: 1, pageSize: 10 },
  filters: UserFeedbackFilters = {}
): Promise<UserFeedbackResponse> => {
  try {
    let query = supabase
      .from("user_feedback")
      .select(`
        *,
        user:profiles!user_feedback_user_id_fkey (
          id,
          email,
          name,
          avatar_url
        )
      `, { count: 'exact' });

    // Apply filters
    if (filters.search) {
      // Search across user name, email, feedback text, and app version
      query = query.or(`
        profiles.name.ilike.%${filters.search}%,
        profiles.email.ilike.%${filters.search}%,
        feedback_text.ilike.%${filters.search}%,
        app_version.ilike.%${filters.search}%
      `);
    }

    if (filters.context && filters.context !== 'all') {
      query = query.eq('context', filters.context);
    }

    if (filters.platform && filters.platform !== 'all') {
      if (filters.platform === 'unknown') {
        query = query.is('platform', null);
      } else {
        query = query.eq('platform', filters.platform);
      }
    }

    if (filters.dateRange && filters.dateRange !== 'all') {
      const now = new Date();
      let dateThreshold: Date;
      
      switch (filters.dateRange) {
        case '7days':
          dateThreshold = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
          break;
        case '30days':
          dateThreshold = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
          break;
        case '3months':
          dateThreshold = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
          break;
        default:
          dateThreshold = new Date(0);
      }
      
      query = query.gte('submitted_at', dateThreshold.toISOString());
    }

    if (filters.hasImages && filters.hasImages !== 'all') {
      if (filters.hasImages === 'yes') {
        query = query.not('image_urls', 'is', null).neq('image_urls', '{}');
      } else if (filters.hasImages === 'no') {
        query = query.or('image_urls.is.null,image_urls.eq.{}');
      }
    }

    // Apply sorting
    const sortBy = filters.sortBy || 'newest';
    switch (sortBy) {
      case 'oldest':
        query = query.order('submitted_at', { ascending: true });
        break;
      case 'name_asc':
        query = query.order('profiles(name)', { ascending: true });
        break;
      case 'name_desc':
        query = query.order('profiles(name)', { ascending: false });
        break;
      case 'context':
        query = query.order('context', { ascending: true });
        break;
      case 'newest':
      default:
        query = query.order('submitted_at', { ascending: false });
        break;
    }

    // Apply pagination
    const { page, pageSize } = pagination;
    const from = (page - 1) * pageSize;
    const to = from + pageSize - 1;
    
    query = query.range(from, to);

    const { data, error, count } = await query;

    if (error) throw error;

    const total = count || 0;
    const totalPages = Math.ceil(total / pageSize);

    return {
      data: (data || []).map(transformUserFeedbackWithUser),
      total,
      totalPages,
      currentPage: page,
      pageSize,
    };
  } catch (error) {
    console.error("Error fetching user feedback:", error);
    throw error;
  }
};

// Get beta testing statistics (admin only)
export const getBetaTestingStats = async (): Promise<BetaTestingStats> => {
  try {
    // Get total testers
    const { count: totalTesters } = await supabase
      .from("beta_testers")
      .select("*", { count: "exact", head: true });

    // Get total feedback from user_feedback table
    const { count: totalFeedback } = await supabase
      .from("user_feedback")
      .select("*", { count: "exact", head: true });

    // Get beta testing feedback count
    const { count: betaFeedback } = await supabase
      .from("user_feedback")
      .select("*", { count: "exact", head: true })
      .eq("context", "beta_testing");

    // Get recent joiners (last 7 days)
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);

    const { count: recentJoiners } = await supabase
      .from("beta_testers")
      .select("*", { count: "exact", head: true })
      .gte("joined_at", sevenDaysAgo.toISOString());

    // Get active testers (last 30 days)
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

    const { count: activeTesters } = await supabase
      .from("beta_testers")
      .select("*", { count: "exact", head: true })
      .gte("joined_at", thirtyDaysAgo.toISOString());

    // Calculate feedback rate
    const feedbackRate = totalTesters && totalTesters > 0 
      ? Math.round((betaFeedback || 0) / totalTesters * 100) 
      : 0;

    return {
      total_testers: totalTesters || 0,
      total_feedback: totalFeedback || 0,
      beta_feedback: betaFeedback || 0,
      recent_joiners: recentJoiners || 0,
      active_testers: activeTesters || 0,
      feedback_rate: feedbackRate,
    };
  } catch (error) {
    console.error("Error fetching beta testing stats:", error);
    throw error;
  }
};

// Delete beta tester (admin only)
export const deleteBetaTester = async (testerId: number): Promise<void> => {
  try {
    const { error } = await supabase
      .from("beta_testers")
      .delete()
      .eq("id", testerId);

    if (error) throw error;
  } catch (error) {
    console.error("Error deleting beta tester:", error);
    throw error;
  }
};

// Get beta testing settings
export const getBetaTestingSettings = async (): Promise<BetaTestingSettings> => {
  try {
    const { data, error } = await supabase
      .from("system_settings")
      .select("key, value")
      .in("key", ["beta_testing_enabled", "beta_testing_message", "beta_testing_end_date"]);

    if (error) throw error;

    const settings: BetaTestingSettings = {
      beta_testing_enabled: false,
      beta_testing_message: "",
      beta_testing_end_date: "",
    };

    data?.forEach((setting) => {
      if (setting.key === "beta_testing_enabled") {
        settings.beta_testing_enabled = setting.value === "true";
      } else if (setting.key === "beta_testing_message") {
        settings.beta_testing_message = setting.value || "";
      } else if (setting.key === "beta_testing_end_date") {
        settings.beta_testing_end_date = setting.value || "";
      }
    });

    return settings;
  } catch (error) {
    console.error("Error fetching beta testing settings:", error);
    return {
      beta_testing_enabled: false,
      beta_testing_message: "",
      beta_testing_end_date: "",
    };
  }
}; 